/**
 * @ngdoc service
 * @name proximus.core.entity:EntityService
 * @requires $entityFactory
 * @requires EntityConfig
 * @requires EntitySubjectPool
 * @requires EntityStore
 * @description
 * # EntityService
 *  Manages CRUD entity operations. When multiple requests for the same entity type is executed
 *  $entityManager lets only one call to be made to a backend and once result is retrieved it
 *  updates all other requested entities of the same type with the value received.
 */
export default class EntityService {
    constructor(entityFactory, entitySubjectPool, entityStore, applicationVersionService, entityIdentifier) {
        this.entityFactory = entityFactory;
        this.entitySubjectPool = entitySubjectPool;
        this.entityStore = entityStore;
        this.applicationVersionService = applicationVersionService;
        this.entityIdentifier = entityIdentifier;
        this.applicationVersionSet = false;
    }
    /**
     * creates new entity for provided entity configuration and parameters
     * @memberof proximus.core.entity:EntityService
     * @param {EntityConfig} configuratio of entity
     * @param {Array} parameters to create entity URL.
     * Parameters must be passed in the order they should appear in the URL placeholders.
     * @returns {Entity} entity
     */
    createEntity(entityConfig, params) {
        return this.entityFactory.createEntity(entityConfig, params);
    }
    /**
     * retrieves entity for provided entity configuration. It automatically loads the index resource
     * to get entities url from if its not yet loaded.
     * @memberof proximus.core.entity:EntityService
     * @param {EntityConfig} configuratio of entity
     * @param {Map} parameters to create entity URL.
     * Parameters must be passed in the order they should appear in the URL placeholders.
     * @returns {Rx.Subject} subject for which entity will be resolved
     */
    // Note: if we create an entity with parameters provided, we still need to specify parameters in the parameterMap.
    // Should we change the code to read the parameters from the entity itself as well?
    getEntity(entityConfig, parameterMap) {
        const entity = this.entityFactory.createEntity(entityConfig, parameterMap);
        let entitySubject = this.entitySubjectPool.getEntitySubject(entity.uniqueKey);
        if (!entitySubject) {
            entitySubject = this.entitySubjectPool.createEntitySubject(entity.uniqueKey);
            this.entityIdentifier.addCustomerIdForPreviewMode(entity).then(entity => {
                if (this.applicationVersionSet) {
                    this.getEntityData(entity);
                }
                else {
                    this.applicationVersionService
                        .clearLocalStorageOnVersionChange()
                        .then(() => {
                        this.applicationVersionSet = true;
                        this.getEntityData(entity);
                    })
                        .catch(error => {
                        console.error('Could not clear application version in storge', error);
                    });
                }
            });
        }
        return entitySubject;
    }
    /**
     * check entity from backend
     * @memberof proximus.core.entity:EntityService
     * @param {Entity} entity to be check
     * @returns {Rx.Subject} subject to which checked entity gets resolved
     */
    headEntity(entity) {
        return this.entityStore.headEntity(entity);
    }
    /**
     * saves entity to backend - POST request
     * @memberof proximus.core.entity:EntityService
     * @param {Entity} entity to be saved
     * @returns {Rx.Subject} subject which gets resolved with saved entity
     */
    saveEntity(entity) {
        return this.entityStore.saveEntity(entity);
    }
    /**
     * updates entity to backend - PUT request
     * @memberof proximus.core.entity:EntityService
     * @param {Entity} entity to be updated
     * @returns {Rx.Subject} subject which gets resolved with updated entity
     */
    updateEntity(entity) {
        return this.entityStore.updateEntity(entity);
    }
    /**
     * deletes entity from backend
     * @memberof proximus.core.entity:EntityService
     * @param {Entity} entity to be deleted
     * @returns {Rx.Subject} subject to which deleted entity gets resolved
     */
    deleteEntity(entity) {
        return this.entityStore.deleteEntity(entity);
    }
    cancelEntityRequest(entity) {
        return this.entityStore.cancelEntityRequest(entity);
    }
    //////////////////
    // PRIVATES
    //////////////////
    getEntityData(entity) {
        const entitySubject = this.entitySubjectPool.getEntitySubject(entity.uniqueKey);
        const entityDataSubject = this.entityStore.getEntityData(entity);
        entityDataSubject.subscribe(entity => entitySubject.next(entity), entity => entitySubject.error(entity), () => entitySubject.complete());
    }
}
